import axios from "axios";
import { apiEndpoint, apiEndpointFunction } from '@iso/lib/helpers/endpoint';
import { authHeader } from '@iso/lib/helpers/authHeader';
import { downloadBlobResponse } from '@iso/lib/helpers/fileDownload';

export const hazardService = {
  getList,
  storeOrUpdateHazard,
  destroy,
  viewHazard,
  getBySeverity,
  getByStatus,
  getByType,
  updateStatus,
  updateSeverity,
  bulkDestroy,
  // New workflow methods
  assignHazard,
  escalateHazard,
  resolveHazard,
  reopenHazard,
  confirmImmediateResolution,
  getEscalationHistory,
  getAssignmentHistory,
  getMyAssignedHazards,
  getOverdueHazards,
  exportHazards,
};

function getList(propertyId){
  return (
    axios
      .get(
        `${apiEndpoint.hazards}?property_id=${propertyId}`,
        {
          headers: authHeader(),
        }
      ).then(res => {
        if(res && res.status === 200 && res.data.code === '0000'){
          return res.data;
        }
      })
  );
}

function storeOrUpdateHazard(data, id = null){
  return (id ? (
    axios
      .put(
        apiEndpointFunction.hazards(id),
        data,
        {
          headers: authHeader(),
        }
      )
  ) : (
    axios
      .post(
        apiEndpoint.hazards,
        data,
        {
          headers: authHeader()
        }
      )
  )).then(res => {
    if(res && res.status === 200){
      return res.data;
    }
    return {};
  });
}

function destroy(id){
  return (
    axios
      .delete(
        apiEndpointFunction.hazards(id),
        {
          headers: authHeader()
        }
      ).then(res => {
        if(res && res.status === 200){
          return res.data;
        }
      })
  );
}

// Bulk destroy method
function bulkDestroy(hazardIds){
  return (
    axios
      .post(
        `${apiEndpoint.hazards}/bulk-destroy`,
        {
          hazard_ids: hazardIds
        },
        {
          headers: authHeader()
        }
      ).then(res => {
        if(res && res.status === 200){
          return res.data;
        }
      })
  );
}

function viewHazard(id){
  return (
    axios
      .get(
        apiEndpointFunction.hazards(id),
        {
          headers: authHeader(),
        }
      ).then(res => {
        if(res && res.status === 200){
          return res.data;
        }
        return {};
      })
  );
}

function getBySeverity(propertyId, severity){
  return (
    axios
      .get(
        `${apiEndpoint.hazards}/by-severity?property_id=${propertyId}&severity=${severity}`,
        {
          headers: authHeader(),
        }
      ).then(res => {
        if(res && res.status === 200 && res.data.code === '0000'){
          return res.data;
        }
      })
  );
}

function getByStatus(propertyId, status){
  return (
    axios
      .get(
        `${apiEndpoint.hazards}/by-status?property_id=${propertyId}&status=${status}`,
        {
          headers: authHeader(),
        }
      ).then(res => {
        if(res && res.status === 200 && res.data.code === '0000'){
          return res.data;
        }
      })
  );
}

function getByType(propertyId, hazardType){
  return (
    axios
      .get(
        `${apiEndpoint.hazards}/by-type?property_id=${propertyId}&hazard_type=${hazardType}`,
        {
          headers: authHeader(),
        }
      ).then(res => {
        if(res && res.status === 200 && res.data.code === '0000'){
          return res.data;
        }
      })
  );
}

function updateStatus(id, status){
  return (
    axios
      .post(
        `${apiEndpoint.hazards}/update-status/${id}`,
        { status },
        {
          headers: authHeader()
        }
      ).then(res => {
        if(res && res.status === 200){
          return res.data;
        }
        return {};
      })
  );
}

function updateSeverity(id, severity){
  return (
    axios
      .post(
        `${apiEndpoint.hazards}/update-severity/${id}`,
        { severity },
        {
          headers: authHeader()
        }
      ).then(res => {
        if(res && res.status === 200){
          return res.data;
        }
        return {};
      })
  );
}

// ============================================
// NEW WORKFLOW METHODS
// ============================================

/**
 * Assign hazard to a user (Manual assignment)
 * @param {number} id - Hazard ID
 * @param {number} assignedToUserId - User ID to assign to
 * @param {number} assignedByUserId - User ID who is assigning (optional, defaults to current user)
 * @param {string} reason - Reason for assignment (optional)
 */
function assignHazard(id, assignedToUserId, assignedByUserId = null, reason = null){
  return (
    axios
      .post(
        `${apiEndpoint.hazards}/${id}/assign`,
        {
          assigned_to_user_id: assignedToUserId,
          assigned_by_user_id: assignedByUserId,
          reason
        },
        {
          headers: authHeader()
        }
      ).then(res => {
        if(res && res.status === 200){
          return res.data;
        }
        return {};
      })
  );
}

/**
 * Manually escalate hazard to next level
 * @param {number} id - Hazard ID
 * @param {string} reason - Reason for manual escalation (required)
 * @param {number} escalatedByUserId - User ID who is escalating (optional, defaults to current user)
 */
function escalateHazard(id, reason, escalatedByUserId = null){
  return (
    axios
      .post(
        `${apiEndpoint.hazards}/${id}/escalate`,
        {
          reason,
          escalated_by_user_id: escalatedByUserId
        },
        {
          headers: authHeader()
        }
      ).then(res => {
        if(res && res.status === 200){
          return res.data;
        }
        return {};
      })
  );
}

/**
 * Rectify (resolve) a hazard
 * @param {number} id - Hazard ID
 * @param {string} resolutionNotes - Rectification notes (required)
 * @param {array} attachmentIds - Array of attachment IDs (optional)
 * @param {number} resolvedByUserId - User ID who is rectifying (optional, defaults to current user)
 * @param {boolean} isClosed - Also close the hazard (prevents auto-escalation)
 */
function resolveHazard(id, resolutionNotes, attachmentIds = [], resolvedByUserId = null, isClosed = false){
  const payload = {
    resolution_notes: resolutionNotes,
    attachment_ids: attachmentIds,
    is_closed: !!isClosed
  };

  // Only include resolved_by_user_id if explicitly provided
  if (resolvedByUserId) {
    payload.resolved_by_user_id = resolvedByUserId;
  }

  return (
    axios
      .post(
        `${apiEndpoint.hazards}/${id}/resolve`,
        payload,
        {
          headers: authHeader()
        }
      ).then(res => {
        if(res && res.status === 200){
          return res.data;
        }
        return {};
      })
  );
}

/**
 * Reopen a resolved hazard
 * @param {number} id - Hazard ID
 * @param {string} reason - Reason for reopening (required)
 * @param {number} reopenedByUserId - User ID who is reopening (optional, defaults to current user)
 */
function reopenHazard(id, reason, reopenedByUserId = null){
  return (
    axios
      .post(
        `${apiEndpoint.hazards}/${id}/reopen`,
        {
          reason,
          reopened_by_user_id: reopenedByUserId
        },
        {
          headers: authHeader()
        }
      ).then(res => {
        if(res && res.status === 200){
          return res.data;
        }
        return {};
      })
  );
}

/**
 * Manager confirms or rejects immediate resolution
 * @param {number} id - Hazard ID
 * @param {boolean} approved - Whether the resolution is approved
 * @param {string} comment - Manager's comment (optional)
 * @param {number} managerId - Manager ID (optional, defaults to current user)
 */
function confirmImmediateResolution(id, approved, comment = null, managerId = null){
  return (
    axios
      .post(
        `${apiEndpoint.hazards}/${id}/confirm-immediate-resolution`,
        {
          approved,
          comment,
          manager_id: managerId
        },
        {
          headers: authHeader()
        }
      ).then(res => {
        if(res && res.status === 200){
          return res.data;
        }
        return {};
      })
  );
}

/**
 * Get escalation history for a hazard
 * @param {number} id - Hazard ID
 */
function getEscalationHistory(id){
  return (
    axios
      .get(
        `${apiEndpoint.hazards}/${id}/escalation-history`,
        {
          headers: authHeader()
        }
      ).then(res => {
        if(res && res.status === 200 && res.data.code === '0000'){
          return res.data;
        }
        return {};
      })
  );
}

/**
 * Get assignment history for a hazard
 * @param {number} id - Hazard ID
 */
function getAssignmentHistory(id){
  return (
    axios
      .get(
        `${apiEndpoint.hazards}/${id}/assignment-history`,
        {
          headers: authHeader()
        }
      ).then(res => {
        if(res && res.status === 200 && res.data.code === '0000'){
          return res.data;
        }
        return {};
      })
  );
}

/**
 * Get hazards assigned to current user
 * @param {number} userId - User ID (optional, defaults to current user)
 * @param {string} status - Filter by status (optional)
 */
function getMyAssignedHazards(userId = null, status = null){
  let url = `${apiEndpoint.hazards}/my-assigned`;
  const params = [];

  if(userId){
    params.push(`user_id=${userId}`);
  }
  if(status){
    params.push(`status=${status}`);
  }

  if(params.length > 0){
    url += `?${params.join('&')}`;
  }

  return (
    axios
      .get(url, {
        headers: authHeader()
      }).then(res => {
        if(res && res.status === 200 && res.data.code === '0000'){
          return res.data;
        }
        return {};
      })
  );
}

/**
 * Get overdue hazards
 * @param {number} propertyId - Property ID (optional)
 * @param {number} level - Escalation level filter (optional)
 */
function getOverdueHazards(propertyId = null, level = null){
  let url = `${apiEndpoint.hazards}/overdue`;
  const params = [];

  if(propertyId){
    params.push(`property_id=${propertyId}`);
  }
  if(level){
    params.push(`level=${level}`);
  }

  if(params.length > 0){
    url += `?${params.join('&')}`;
  }

  return (
    axios
      .get(url, {
        headers: authHeader()
      }).then(res => {
        if(res && res.status === 200 && res.data.code === '0000'){
          return res.data;
        }
        return {};
      })
  );
}

/**
 * Download the property's hazards as a CSV. Admin-only server side — a
 * non-admin caller gets a 403 back rather than a file.
 *
 * @param {number} propertyId
 * @param {Object} filters Optional severity / status / hazard_type / search,
 *   matching the list screen so the file mirrors what is on screen.
 * @returns {Promise<void>}
 */
function exportHazards(propertyId, filters = {}) {
  return axios
    .get(`${apiEndpoint.hazards}/export`, {
      params: { property_id: propertyId, ...filters },
      headers: { ...authHeader(), Accept: 'text/csv' },
      responseType: 'blob',
    })
    .then((res) => {
      downloadBlobResponse(res, `hazards-${propertyId}.csv`);
    });
}
